/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright (c) 2017 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import React from "react";
import PropTypes from "prop-types";

import SearchSuggestions from "../SearchSuggestions/SearchSuggestions.js";
import Icon from "../Icon";

import styles from "./searchBar.module.scss";
import { generateId } from "../_util/common.js";

const ENTER_KEY = 13;
const ESC_KEY = 27;
const DOWN_ARROW_KEY = 40;
const UP_ARROW_KEY = 38;
const SPACE_KEY = 32;

let timeout = null;

// https://stackoverflow.com/questions/152975/how-do-i-detect-a-click-outside-an-element
class SearchBar extends React.Component {
  static propTypes = {
    label: PropTypes.string,
    searchText: PropTypes.string,
    placeholder: PropTypes.string,
    histories: PropTypes.array,
    suggestions: PropTypes.array,
    onChange: PropTypes.func,
    onSearchClick: PropTypes.func,
    onSuggestionClick: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onClearText: PropTypes.func,
    type: PropTypes.string.isRequired,
    id: PropTypes.string,
    ariaLabelDropdown: PropTypes.string,
  };

  static defaultProps = {
    label: "Search",
    searchText: "",
    type: "large",
    onBlur: () => {/**/},
    onFocus: () => {/**/},
    onClearText: () => {/**/},
    id: generateId(),
    histories: [],
    suggestions: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      value: this.props.searchText,
      suggestionHidden: true,
      focusedIndex: -1,
      selectedIndex: 0,
      inputFocus: false,
      searchBtnCSS: "",
      searchInputCSS: "",
    };
  }

  handleChange = () => {
    this.showSuggestions();
    this.setState(
      {
        value: this.searchInput.value,
        focusedIndex: -1,
      },
      this.onChangeCallback
    );
  };

  onChangeCallback = () => {
    clearTimeout(timeout);
    const _onChange = this.props.onChange;
    const _value = this.state.value;

    timeout = setTimeout(function () {
      if (_onChange) {
        _onChange(_value);
      }
    }, 500);
  };

  handleInputKeyDown = (event) => {
    const key = event.which || event.keyCode || 0;

    switch (key) {
      case ENTER_KEY:
        if (this.props.onSearchClick) {
          this.props.onSearchClick(this.state.value);
          if (!this.state.suggestionHidden) {
            this.hideSuggestions();
          } else {
            this.showSuggestions();
            this.focusOnSuggestion(-1);
          }
        }
        break;
      case ESC_KEY:
        this.hideSuggestions();
        break;
      case DOWN_ARROW_KEY:
        if (this.state.suggestionHidden) {
          this.showSuggestions();
          this.focusOnSuggestion(-1);
        } else {
          const next = this.getNextSuggestion();
          this.focusOnSuggestion(next);
        }
        event.preventDefault();
        break;
      case UP_ARROW_KEY:
        if (this.state.suggestionHidden) {
          this.showSuggestions();
          this.focusOnSuggestion(+1);
        } else {
          const previous = this.getPreviousSuggestion();
          this.focusOnSuggestion(previous);
        }
        event.preventDefault();
        break;
      default:
        break;
    }
  };

  handleInputKeyUp = (e) => {
    e.stopPropagation();
  };

  handleListKeyDown = (event, value, index) => {
    const key = event.which || event.keyCode || 0;
    switch (key) {
      case DOWN_ARROW_KEY:
        const next = this.getNextSuggestion();
        this.focusOnSuggestion(next);
        event.preventDefault();
        break;
      case UP_ARROW_KEY:
        const previous = this.getPreviousSuggestion();
        this.focusOnSuggestion(previous);
        event.preventDefault();
        break;

      case SPACE_KEY:
      case ENTER_KEY:
        this.handleSuggestionClick(value, index);
        break;
      case ESC_KEY:
        this.hideSuggestions();
        break;
      default:
        break;
    }
  };

  getNextSuggestion = () => {
    return (this.state.focusedIndex + 1) % this.totalNumberOfSuggestions();
  };

  getPreviousSuggestion = () => {
    const total = this.totalNumberOfSuggestions();
    if (this.state.focusedIndex === -1) {
      return total - 1;
    } else {
      return (this.state.focusedIndex + total - 1) % total;
    }
  };

  focusOnSuggestion = (index) => {
    this.setState({
      focusedIndex: index,
    });
  };

  totalNumberOfSuggestions = () => {
    return (
      this.props.histories.length +
      this.props.suggestions.reduce((sum, suggestion) => {
        return sum + suggestion.items.length;
      }, 0)
    );
  };

  handleSearchClick = (e) => {
    e.stopPropagation()
    if (this.props.onSearchClick) {
      this.props.onSearchClick(this.state.value);
      this.hideSuggestions();
    }
    this.setState(
      {
        inputFocus: true,
        suggestionHidden: true,
      },
      () => {/**/}
    );
  };

  resetInput = (event) => {
    if(event) {
      event.stopPropagation();
    }
    this.setState(
      {
        value: "",
        selectedIndex: 0,
      },
      () => {
        this.props.onClearText(event);
        this.searchInput.focus();
      }
    );
  };

  resetFocusedIndex = () => {
    this.setState({
      focusedIndex: -1,
    });
  };

  hideSuggestions = () => {
    document.removeEventListener("click", this.handleClickOutside);
    this.setState(
      {
        suggestionHidden: true,
        focusedIndex:
          this.state.selectedIndex !== null ? this.state.selectedIndex : -1,
      },
      () => {
        this.searchInput.focus();
      }
    );
  };

  handleClickOutside = () => {
    document.removeEventListener("click", this.handleClickOutside);
    this.setState({
      suggestionHidden: true,
      focusedIndex:
        this.state.selectedIndex !== null ? this.state.selectedIndex : -1,
    });
  };

  showSuggestions = (event) => {
    if(event) {
      event.stopPropagation();
    }
    
    if (this.state.suggestionHidden) {
      document.addEventListener("click", this.handleClickOutside);
      this.setState(
        {
          suggestionHidden: false,
          focusedIndex:
            this.state.selectedIndex !== null
              ? this.state.selectedIndex - 1
              : -1,
        },
        () => {
          const { onFocus } = this.props;
          if ((onFocus, event)) {
            onFocus(event);
          }
        }
      );
    }
  };

  handleSuggestionClick = (value, index) => {
    this.setState(
      {
        value: value,
        selectedIndex: index,
      },
      () => {
        this.searchInput.focus();
        this.hideSuggestions();
        this.props.onSuggestionClick(value);
      }
    );
  };

  handleOnBlur = () => {
    this.setState({
      inputFocus: false,
    });
  };

  setFocus = () => {
    this.setState({
      inputFocus: true,
    });
  };

  generateSearchInputStyles = () => {
    return this.props.type === "large"
      ? `${styles.search_input} ${styles.search_input_large}`
      : `${styles.search_input} ${styles.search_input_medium}`;
  };

  generateSearchBtnStyles = () => {
    return this.props.type === "large"
      ? `${styles.search_btn} ${styles.search_btn_large}`
      : `${styles.search_btn} ${styles.search_btn_medium}`;
  };

  render() {
    const { placeholder, histories, suggestions } = this.props;

    const { value, suggestionHidden, focusedIndex, inputFocus } = this.state;
    const suggestionsVisible =
      (!suggestionHidden &&
        (suggestions.length !== 0 || histories.length !== 0)) ||
      false;
    return (
      <div
        role="search"
        className={`${styles.search_container}`}
        aria-expanded={suggestionsVisible ? true : false}
        aria-owns={`${this.props.id}-popup`}
        aria-haspopup="listbox"
      >
        <div className={styles.search_box}>
          <input
            className={this.generateSearchInputStyles()}
            type="text"
            ref={(input) => {
              this.searchInput = input;
            }}
            aria-label={`search box ${
              suggestionsVisible && "search history and suggestion list open"
            }`}
            autoComplete="off"
            onChange={this.handleChange}
            onKeyDown={this.handleInputKeyDown}
            onKeyUp={this.handleInputKeyUp}
            value={this.state.value}
            placeholder={placeholder}
            onClick={this.showSuggestions}
            onFocus={this.setFocus}
            aria-autocomplete="list"
            onBlur={this.handleOnBlur}
          />

          <div className={styles.search_btn_container}>
            <button
              className={styles.search_clear_icon}
              style={{ display: value ? "block" : "none" }}
              aria-label="Clear search text"
              onClick={this.resetInput}
              onFocus={this.resetFocusedIndex}
            >
              <Icon
                isHidden={true}
                fileName="remove-sm-18"
                title="Remove"
                name={`${generateId()}-remove`}
              />
            </button>
            <button
              type="button"
              className={`${this.generateSearchBtnStyles()} ${
                suggestionsVisible || inputFocus
                  ? styles.search_btn_highlighted
                  : ""
              }`}
              disabled={!value}
              aria-label="Search"
              onClick={this.handleSearchClick}
              ref={(button) => {
                this.searchButton = button;
              }}
              tabIndex="-1"
            >
              <Icon
                isHidden={true}
                fileName="search-18"
                name={`${generateId()}-magnifying-glass`}
                title="Search"
                focusable={false}
              />
            </button>
          </div>
        </div>
        {suggestionsVisible && (
          <SearchSuggestions
            searchText={this.state.value}
            histories={histories}
            suggestions={suggestions}
            focusedIndex={focusedIndex}
            onClick={this.handleSuggestionClick}
            onKeyDown={this.handleListKeyDown}
            onBlur={this.hideSuggestions}
            id={this.props.id}
            placeholder={this.props.ariaLabelDropdown}
          />
        )}
      </div>
    );
  }
}

export default SearchBar;
